import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailablBookingStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportBookingStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchBookingReportStation,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setBookingStationReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportBookingStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * Reports management page
 */
function BookingReportTableStation(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { leftTopContent, preferenceFilter } = properties;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isBookingStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportBookingStation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailablBookingStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailablBookingStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailablBookingStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailablBookingStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailablBookingStationReport,
      sortAllow: true,
    },
    {
      label: t('totalBookings'),
      accessorKey: 'totalBooking' as keyof AvailablBookingStationReport,
      sortAllow: true,
    },
    {
      label: t('completed'),
      accessorKey: 'completed' as keyof AvailablBookingStationReport,
      sortAllow: true,
    },

    {
      label: t('cancelled'),
      accessorKey: 'canceled' as keyof AvailablBookingStationReport,
      sortAllow: true,
    },
  ];
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchBookingReportStation({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
    });
  };
  /**
   * Current columns for Booking Station Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('totalBookings'), accessorKey: 'totalBooking' },
    { label: t('completed'), accessorKey: 'completed' },
    { label: t('cancelled'), accessorKey: 'canceled' }, // backend key
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });
  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchBookingStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: AvailablBookingStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount ?? '0';

              case 'totalBooking':
                return row?.totalBooking ?? '0';

              case 'completed':
                return row?.completed ?? '0';

              case 'canceled':
                return row?.canceled ?? '0';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param value
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isBookingStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setBookingStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isBookingStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'bookingsReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          sizeChangeButtonRequired
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={window.innerHeight >= 700 ? 320 : 339}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          ReferenceCurrentPages={ReferenceCurrentPages}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
    </div>
  );
}

export default BookingReportTableStation;
